import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { SkeletonUtils } from 'three-stdlib'
import { cloneAndScaleUV, setupTextureRepeat } from "../../utils/geometryUltis"
import useGateBoundingDataChecker from "../../utils/gateBoundingDataChecker"

const BramaRolowana = forwardRef(({ gate, onUpdate, textures, ...props }, ref) => {
  const group = useRef()
  const { scene, animations } = useGLTF('/models/gates/Brama-rolowana.glb')
  const cloned = useRef()

  // Clone + UV scale + materials
  useEffect(() => {
    if (!scene) return

    const newClone = SkeletonUtils.clone(scene)

    // ✅ nie używamy materials.Material (materials jest dopiero niżej w useMemo)
    const isTargetMaterial = (m) => !!m && m.name === 'Material'

    const applyRepeatFromGC = (t, gc) => {
      if (!t) return
      setupTextureRepeat(
        t,
        1 / gc.multiplierHorizontal,
        1 / gc.multiplierVertical
      )
    }

    // ✅ globalny switch: wood = skalujemy UV (tiling), metal_default = nie skalujemy (stretch)
    const isWoodGlobal = textures?.gates_roller_color?.materialType === 'wooden'

    newClone.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry || !obj.material) return

      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      const touchesTarget = mats.some(isTargetMaterial)
      if (!touchesTarget) return

      // 1) Geometria: klon + skalowanie UV (dla wood), brak skalowania (dla metal_default)
      const clonedGeometry = obj.geometry.clone()
      const scaledGeometry = cloneAndScaleUV(
        clonedGeometry,
        1, 1, 1,
        isWoodGlobal ? (gate.width / 100) : 1,
        isWoodGlobal ? (gate.height / 100) : 1
      )
      obj.geometry = scaledGeometry

      // 2) Materiały
      const newMats = mats.map((mat) => {
        if (!isTargetMaterial(mat)) return mat

        const m = mat.clone()

        if (textures && textures.gates_roller_color) {
          const gc = textures.gates_roller_color
          const isWood = gc.materialType === 'wooden'

          if (isWood) {
            // ✅ WOOD: jak w segmentowej (map + normal + roughness)
            const diffBase = gc.textureDiff || null
            const norBase =
              gc.textureNormalness ||
              gc.textureNormal ||
              gc.textureNormalMap ||
              null

            m.map = diffBase ? (diffBase.clone?.() ?? diffBase) : null
            m.normalMap = null

            m.roughnessMap = null
            m.metalnessMap = null

            m.metalness = 0
            m.roughness = typeof gc.roughnessValue === 'number' ? gc.roughnessValue : 0.6

            ;[m.map, m.normalMap].forEach((t) => applyRepeatFromGC(t, gc))
          } else {
            // ✅ PLASTIC: baseColor + normal (bez roughnessMap)
            const plasticBase =
              textures?.plastic_default?.textureDiff ||
              textures?.plastic_default?.texture ||
              null;

            const plasticNormal =
              textures?.plastic_default?.textureNormalness ||
              textures?.plastic_default?.textureNormal ||
              textures?.plastic_default?.textureNormalMap ||
              null;

            m.map = plasticBase ? (plasticBase.clone?.() ?? plasticBase) : null;
            m.normalMap = plasticNormal ? (plasticNormal.clone?.() ?? plasticNormal) : null;

            // ✅ brak map metal/rough jeśli nie masz
            m.roughnessMap = null;
            m.metalnessMap = null;

            // ✅ plastik = metalness 0
            m.metalness = 0.0;
            m.roughness = 0.6;         // podbij żeby nie błyszczało
            m.envMapIntensity = 0.35;   // zbija odbicia z HDRI (często kluczowe)

            // ✅ normal może być za mocny -> zmniejsz
            if (m.normalMap) {
              m.normalScale = new THREE.Vector2(0.35, 0.35);
            }

            // ✅ bezpieczny repeat (UWAGA: nie dawaj 0)
            if (m.map) {
              setupTextureRepeat(m.map, 1, 1);
              m.map.offset.set(0, 0);
            }
            if (m.normalMap) {
              setupTextureRepeat(m.normalMap, 1, 1);
              m.normalMap.offset.set(0, 0);
            }

            if (m.color) m.color.set(gc.hex);
          }

          if (m.color) m.color = new THREE.Color(gc.hex)
          m.needsUpdate = true
        }

        return m
      })

      obj.material = Array.isArray(obj.material) ? newMats : newMats[0]
    })

    cloned.current = newClone
    group.current.clear()
    group.current.add(newClone)
  }, [scene, gate.width, gate.height, props.length, props.width, textures])

  // ✅ Offset jak u Ciebie, ale:
  // - robimy go TYLKO dla wood (żeby "zszyć" lamele)
  // - NIE od dołu do góry -> odwracamy kierunek (offset.y -= i * step)
  // - metal_default: zero offsetu
  const { nodes, materials } = useMemo(() => {
    const nodes = {}
    const materials = {}

    if (cloned.current) {
      const isWoodGlobal = textures?.gates_roller_color?.materialType === 'wooden'

      const offsetStep = 0.0333
      let i = 0

      cloned.current.traverse((obj) => {
        if (!obj.isMesh || !obj.material) return

        obj.material = obj.material.clone()

        const cloneTex = (key) => {
          const t = obj.material[key]
          if (!t) return null
          obj.material[key] = t.clone()
          obj.material[key].needsUpdate = true
          return obj.material[key]
        }

        const map = cloneTex('map')
        const normalMap = cloneTex('normalMap')
        const roughnessMap = cloneTex('roughnessMap')

        if (isWoodGlobal) {
          // ✅ to samo co miałeś, tylko odwrócony kierunek (nie "od dołu do góry")
          if (map) map.offset.y -= i * offsetStep
          if (normalMap) normalMap.offset.y -= i * offsetStep
          if (roughnessMap) roughnessMap.offset.y -= i * offsetStep
        } else {
          // ✅ metal_default: żadnych offsetów
          if (map) map.offset.set(0, 0)
          if (normalMap) normalMap.offset.set(0, 0)
          if (roughnessMap) roughnessMap.offset.set(0, 0)
        }

        i++
      })
    }

    return { nodes, materials }
  }, [cloned.current, textures])

  const [scaledAnimations, setScaledAnimations] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const mixerRef = useRef(null)

  useImperativeHandle(ref, () => group.current)

  // Clone animations
  useEffect(() => {
    if (!group.current) return

    const newScaledAnimations = animations.map((clip) => {
      const clonedTracks = clip.tracks.map((track) => {
        if (track.name.endsWith(".position")) {
          const times = Array.from(track.times)
          const values = Array.from(track.values)
          for (let i = 0; i < values.length; i += 3) {
            values[i] = values[i]
            values[i + 1] = values[i + 1]
            values[i + 2] = values[i + 2]
          }
          return new THREE.VectorKeyframeTrack(track.name, times, values)
        }
        return track.clone()
      })
      return new THREE.AnimationClip(clip.name, -1, clonedTracks)
    })

    setScaledAnimations(newScaledAnimations)
  }, [animations, gate.width, gate.height, gate.position, props.width, props.length, textures])

  // Mixer setup
  useEffect(() => {
    if (!group.current) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(group.current)
    }

    mixerRef.current = new THREE.AnimationMixer(group.current)

    scaledAnimations.forEach(clip => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
    })
  }, [scaledAnimations])

  // Animation loop
  useEffect(() => {
    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  // Reset animation when position changes
  useEffect(() => {
    scaledAnimations.forEach(clip => {
      const action = mixerRef.current.clipAction(clip)
      action.reset()
      action.stop()
    })

    setIsOpen(false)
    isOpenRef.current = false
    isAnimatingRef.current = false
  }, [gate.position])

  // Bounding box updater
  useGateBoundingDataChecker({
    nodes,
    gate,
    onBoundingDataChanged: () => {
      if (isAnimatingRef.current) return

      const box = new THREE.Box3().setFromObject(group.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const updateData = {
        boundingData: { box, size }
      }

      if (!isOpen) {
        const [x = 0, y = 0, z = 0] = [gate.position.x, 0, gate.position.z]
        const adjustedY = y + gate.height / 100 / 2
        const adjustedX = x - gate.height / 100 / 2

        updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
        updateData.positionAdjusted = [adjustedX, adjustedY, z]
        onUpdate(updateData)
      }
    },
    group,
  })

  // Click to open/close
  const handleClick = () => {
    if (!mixerRef.current || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)
    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpen) {
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    setIsOpen(!isOpen)
  }

  return (
    <group
      ref={group}
      wall={'front_wall'}
      dispose={null}
      position={[gate.position.x, gate.position.y, gate.position.z]}
      castShadow={true}
      scale={[gate.height / 100, gate.height / 100, gate.width / 100]}
      onClick={handleClick}
    />
  )
})

export default BramaRolowana

useGLTF.preload('/models/gates/Brama-rolowana.glb')
